// Surface lookup + wall collision against the densified track caches.
// Authored geometry is densified once at track-load by
// shared/track-geometry; per-frame queries walk the cached polylines.
// Same Catmull-Rom math as the editor — what you see in the editor is
// exactly what you collide with.
//
// Hot-path strategy: every query goes through geometry.spatialIndex
// (built once at track-load). For a query at point P we touch only
// the cell containing P (and 8 neighbors for distance queries) instead
// of scanning every wall, every patch, the full centerline. With 40
// cars on mid-ohio that's a ~30× per-tick reduction in segment checks.

import {
  closestPointOnSegmentInto,
  nearestPointOnDense,
  pointInPolygon,
} from '../../shared/geometry.js';
import { validSurface, patchLayer } from '../../shared/surfaces.js';
import { applyImpactDamage } from './condition.js';

// Approximate car body radius for wall-collision push-back. The car is
// treated as a circle — slightly conservative when sliding along walls
// at angle, but cleanly prevents visual clipping through thin walls.
// Oriented-body collision (SAT against the wall spline) is a future
// polish pass when damage modelling is worth the complexity.
export const CAR_BODY_RADIUS = 1.2;

// Module-scoped scratch for the per-segment closest-point check inside
// hot loops. Reused across calls; safe because every consumer reads
// the result before the next call clobbers it.
const _segScratch = { dist: 0, t: 0, point: { x: 0, y: 0 } };

// Per-wall best-hit scratch for applyWallCollision. Grown to walls.length
// on first use; reset to Infinity at the start of each call (cheap O(W)
// for typical W ≤ 50). Avoids allocating a Map per car per tick.
let _wallScratchDist = null;
let _wallScratchPx   = null;
let _wallScratchPy   = null;
function ensureWallScratch(n) {
  if (_wallScratchDist && _wallScratchDist.length >= n) return;
  _wallScratchDist = new Float64Array(n);
  _wallScratchPx   = new Float64Array(n);
  _wallScratchPy   = new Float64Array(n);
}

// What surface is the car standing on? Resolution walks the
// renderer's layers in visual top-down order so the simulator
// reports the same surface that's painted on top at any world point:
//   1. Strip — within local half-width of the centerline → tarmac.
//      Drawn last among the racing surfaces, so curbs/patches that
//      bleed onto it are hidden — physics agrees: still tarmac.
//   2. 'above' patches (curb) — painted on top of tarmac patches but
//      under the strip; override merge/below outside the strip.
//   3. Pit lane spline + merge ribbons — tarmac.
//   4. 'merge' patches (tarmac) — paved extensions; only matter past
//      the strip's edge since both report 'tarmac' on the strip.
//   5. 'below' patches (grass, gravel, sand) — off-track runoff,
//      only reported when no higher layer hits.
//   6. Track default surface (typically grass).
//
// geometry.patches is sorted ascending by SURFACE_PRIORITY at track-
// load (track-geometry.js); the per-cell patch list is in patches[]
// order, so iterating it in REVERSE gives highest-priority match first
// within a layer.
export function surfaceAt(pos, track, geometry) {
  const idx = geometry.spatialIndex;
  if (!idx) return surfaceAtNoIndex(pos, track, geometry);

  const cellKeyFn = idx.key;
  const gx = Math.floor(pos.x / idx.cellSize);
  const gy = Math.floor(pos.y / idx.cellSize);
  const cellKey = cellKeyFn(gx, gy);
  const patchList = idx.patchCells.get(cellKey);
  const patches   = geometry.patches;

  // 1. Strip — local half-width matches the variable-width visual.
  // Checked first because the strip is the visually-topmost racing
  // surface; a curb or tarmac patch that bleeds onto the strip is
  // hidden under it, and physics matches that.
  const cl = nearestKindInCells(idx, geometry, pos, 'centerline', cellKeyFn, gx, gy);
  if (cl.segIdx >= 0) {
    const halfArr = geometry.centerlineEdges?.halfWidths;
    const localHalf = halfArr?.[cl.segIdx]
      ?? (track.trackline?.width ?? 16) / 2;
    if (cl.dist <= localHalf) return 'tarmac';
  }

  // 2. Above-strip (curb) — outside the strip, curbs paint over
  // tarmac patches at corner apexes.
  if (patchList) {
    for (let i = patchList.length - 1; i >= 0; i--) {
      const patch = patches[patchList[i]];
      if (patchLayer(patch) !== 'above') continue;
      if (patchHits(patch, pos)) return validSurface(patch.surface);
    }
  }

  // 3. Pit lane spline + merge ribbons.
  if (geometry.pitLane) {
    const pl = nearestKindInCells(idx, geometry, pos, 'pitLane', cellKeyFn, gx, gy);
    if (pl.segIdx >= 0) {
      const localHalf = geometry.pitLane.halfWidths?.[pl.segIdx]
        ?? geometry.pitLane.halfWidth;
      if (pl.dist <= localHalf) return 'tarmac';
    }
  }
  // Ribbons: small (entry/exit only) and the per-ribbon halfWidth is
  // looked up by ownerIdx, so handle them with one nearest-pass per
  // ribbon — cheap given there are at most two.
  for (let ri = 0; ri < geometry.ribbons.length; ri++) {
    const ribbon = geometry.ribbons[ri];
    if (!bboxNearPoint(ribbon.bbox, pos, ribbon.halfWidth)) continue;
    const r = nearestRibbonInCells(idx, geometry, pos, ri, cellKeyFn, gx, gy);
    if (r.segIdx >= 0 && r.dist <= ribbon.halfWidth) return 'tarmac';
  }

  // 4. Merge (tarmac patches).
  if (patchList) {
    for (let i = patchList.length - 1; i >= 0; i--) {
      const patch = patches[patchList[i]];
      if (patchLayer(patch) !== 'merge') continue;
      if (patchHits(patch, pos)) return validSurface(patch.surface);
    }
  }

  // 5. Below-strip patches (sand, gravel, grass).
  if (patchList) {
    for (let i = patchList.length - 1; i >= 0; i--) {
      const patch = patches[patchList[i]];
      if (patchLayer(patch) !== 'below') continue;
      if (patchHits(patch, pos)) return validSurface(patch.surface);
    }
  }

  return validSurface(track.defaultSurface || 'grass');
}

// Single-patch point test. Closed patches use point-in-polygon;
// open-band patches use distance-to-spline within thickness/2. Bbox
// early-out skips the polygon walk for far-away patches.
function patchHits(patch, pos) {
  const bb = patch.bbox;
  if (bb && (pos.x < bb.minX || pos.x > bb.maxX || pos.y < bb.minY || pos.y > bb.maxY)) {
    return false;
  }
  if (patch.closed) return pointInPolygon(pos, patch.dense);
  const np = nearestPointOnDense(pos, patch.dense, false);
  return !!(np && np.dist <= patch.thickness / 2);
}

// Walk segments in the 3×3 cell neighborhood and return the closest
// one of `kind`. Returns a {dist, segIdx} pair (dist=Infinity, segIdx=-1
// when none). For 'centerline' and 'pitLane' there's only one owner
// (ownerIdx === 0) so we don't need to track which polyline matched.
function nearestKindInCells(idx, geometry, pos, kind, cellKeyFn, gx, gy) {
  let bestDist = Infinity, bestSegIdx = -1;
  const polylineDense = kind === 'centerline'
    ? geometry.centerline
    : kind === 'pitLane'
      ? geometry.pitLane?.dense
      : null;
  if (!polylineDense) return { dist: bestDist, segIdx: bestSegIdx };
  const closed = kind === 'centerline';
  const n = polylineDense.length;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const arr = idx.segCells.get(cellKeyFn(gx + dx, gy + dy));
      if (!arr) continue;
      for (let i = 0; i < arr.length; i++) {
        const e = arr[i];
        if (e.kind !== kind) continue;
        const a = polylineDense[e.segIdx];
        const b = polylineDense[(e.segIdx + 1) % n];
        closestPointOnSegmentInto(pos, a, b, _segScratch);
        if (_segScratch.dist < bestDist) {
          bestDist   = _segScratch.dist;
          bestSegIdx = e.segIdx;
          if (!closed && (bestSegIdx === n - 1)) {
            // ok — open polyline guard handled by build-time segCount
          }
        }
      }
    }
  }
  return { dist: bestDist, segIdx: bestSegIdx };
}

// Nearest segment of a specific ribbon (by ownerIdx). Mirrors
// nearestKindInCells but filters on ribbon owner.
function nearestRibbonInCells(idx, geometry, pos, ownerIdx, cellKeyFn, gx, gy) {
  let bestDist = Infinity, bestSegIdx = -1;
  const ribbon = geometry.ribbons[ownerIdx];
  if (!ribbon) return { dist: bestDist, segIdx: bestSegIdx };
  const dense = ribbon.dense;
  const n = dense.length;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const arr = idx.segCells.get(cellKeyFn(gx + dx, gy + dy));
      if (!arr) continue;
      for (let i = 0; i < arr.length; i++) {
        const e = arr[i];
        if (e.kind !== 'ribbon' || e.ownerIdx !== ownerIdx) continue;
        const a = dense[e.segIdx];
        const b = dense[(e.segIdx + 1) % n];
        closestPointOnSegmentInto(pos, a, b, _segScratch);
        if (_segScratch.dist < bestDist) {
          bestDist   = _segScratch.dist;
          bestSegIdx = e.segIdx;
        }
      }
    }
  }
  return { dist: bestDist, segIdx: bestSegIdx };
}

// Project a position onto the dense centerline (via the spatial
// index) and return its arc length around the lap. Returns -1 when
// no centerline cell hit the query (rare — implies the car teleported
// far outside the indexed area). Cheap: walks the 3×3 neighborhood
// only.
export function centerlineArcAt(pos, geometry) {
  const idx = geometry.spatialIndex;
  if (!idx || !geometry.centerlineArcs) return -1;
  const cellKeyFn = idx.key;
  const gx = Math.floor(pos.x / idx.cellSize);
  const gy = Math.floor(pos.y / idx.cellSize);
  const r = nearestKindInCells(idx, geometry, pos, 'centerline', cellKeyFn, gx, gy);
  if (r.segIdx < 0) return -1;
  // Re-run closestPoint to recover `t` for arc interpolation. Only
  // touched once per call so the cost is trivial.
  const dense = geometry.centerline;
  const a = dense[r.segIdx];
  const b = dense[(r.segIdx + 1) % dense.length];
  closestPointOnSegmentInto(pos, a, b, _segScratch);
  const arcs = geometry.centerlineArcs;
  return arcs.cumulative[r.segIdx] + _segScratch.t * arcs.segLengths[r.segIdx];
}

// Where is `pos` relative to the nearer track edge? Used by the AI
// controller for self-preservation (slow + steer in when about to drift
// off track) and neighbor courtesy (yield when an alongside rival is
// closer to the edge than we are). Without this, the AI plans against
// racing-line curvature only — happy to overshoot at full throttle and
// rely on the racing line itself to be safe — and on side-by-side
// running, locks its own lateral position regardless of whether the
// other car has any tarmac left.
//
// Returns null when no path cache exists (degenerate track) or the
// position is far outside the spatial index.
//   segIdx          path segment used for the projection.
//   distFromCenter  unsigned meters from pos to the path centerline.
//   halfWidth       local half-width at this segment.
//   distToEdge      halfWidth − distFromCenter. Positive on path,
//                   negative when already past the edge into runoff.
//   inwardX/Y       unit vector pointing from pos back toward the
//                   path centerline. Zero when pos is exactly on it.
//
// `opts.path` selects which centerline to project against:
//   'racing' (default) — racing-surface centerline + centerlineEdges.
//                        Uses the spatial index for O(1) cell lookup.
//   'pit'              — pit-route open polyline (entry ribbon + pit-
//                        lane spline + exit ribbon, halfWidths included).
//                        Linear nearestPointOnDense scan; cheap on a
//                        few hundred points and only run for cars
//                        actually pitting.
//
// The active-path split is what lets pitting cars get the same edge-
// aware self-preservation behavior they get on the racing surface.
// A pit-lane car projected onto the RACING centerline reads as
// deeply off-track (pit lane is, by design, off the racing surface);
// closenessP would saturate and the AI would slow + steer toward the
// racing surface. Wrong reference frame. Projecting onto the pit-
// route centerline gives the AI the right "am I drifting off the
// path I'm supposed to be on?" signal.
export function edgeProximity(pos, geometry, opts = {}) {
  const pathName = opts.path ?? 'racing';
  let dense, halfWidthsArr, isClosed, useSpatial;
  if (pathName === 'pit') {
    const route = geometry?.pitRoute;
    if (!route || !route.dense || route.dense.length < 2) return null;
    dense = route.dense;
    halfWidthsArr = route.halfWidths;
    isClosed = false;
    useSpatial = false;
  } else {
    dense = geometry?.centerline;
    halfWidthsArr = geometry?.centerlineEdges?.halfWidths;
    isClosed = true;
    useSpatial = true;
  }
  if (!dense || dense.length < 2) return null;

  let segIdx = -1;
  if (useSpatial) {
    const idx = geometry.spatialIndex;
    if (idx) {
      const cellKeyFn = idx.key;
      const gx = Math.floor(pos.x / idx.cellSize);
      const gy = Math.floor(pos.y / idx.cellSize);
      const r = nearestKindInCells(idx, geometry, pos, 'centerline', cellKeyFn, gx, gy);
      segIdx = r.segIdx;
    }
  }
  if (segIdx < 0) {
    const np = nearestPointOnDense(pos, dense, isClosed);
    if (!np) return null;
    segIdx = np.segmentIdx;
  }
  const a = dense[segIdx];
  const b = dense[isClosed ? (segIdx + 1) % dense.length : segIdx + 1];
  if (!b) return null;
  closestPointOnSegmentInto(pos, a, b, _segScratch);
  const dx = pos.x - _segScratch.point.x;
  const dy = pos.y - _segScratch.point.y;
  const distFromCenter = Math.hypot(dx, dy);
  const halfWidth = halfWidthsArr?.[segIdx] ?? 8;
  const inwardX = distFromCenter > 1e-6 ? -dx / distFromCenter : 0;
  const inwardY = distFromCenter > 1e-6 ? -dy / distFromCenter : 0;
  return {
    segIdx,
    distFromCenter,
    halfWidth,
    distToEdge: halfWidth - distFromCenter,
    inwardX,
    inwardY,
  };
}

// Resolve the car's current track-context labels from a centerline
// arc. Returns { corner, area } where each is the authored text
// string of the nearest hit, or null when nothing matches.
//
//   - Corner: nearest corner label whose arc is within
//             CORNER_PROXIMITY_M of `arc` (forward or backward
//             around the lap, whichever is shorter).
//   - Area:   first area label whose forward span [startArc, endArc]
//             contains `arc` (handles wraparound naturally —
//             startArc > endArc means the area straddles S/F).
//
// Both lookups are O(N labels) — typical tracks have <30 labels, so
// this is a few microseconds per call. Suitable for HUD/announcer
// systems that update at ~6 Hz.
const CORNER_PROXIMITY_M = 35;
export function locationAtArc(arc, geometry) {
  const labels = geometry.labels;
  const arcs   = geometry.centerlineArcs;
  if (!labels || !labels.length || !arcs || arcs.totalLength <= 0) {
    return { corner: null, area: null };
  }
  const total = arcs.totalLength;
  // Normalize arc to [0, total).
  let a = arc % total;
  if (a < 0) a += total;

  let bestCornerText = null;
  let bestCornerDist = CORNER_PROXIMITY_M;
  let areaText = null;
  for (let i = 0; i < labels.length; i++) {
    const L = labels[i];
    if (L.kind === 'corner') {
      // Shorter of the two ways around the loop.
      let d = Math.abs(L.arc - a);
      if (d > total / 2) d = total - d;
      if (d < bestCornerDist) {
        bestCornerDist = d;
        bestCornerText = L.text;
      }
    } else if (L.kind === 'area' && areaText == null) {
      // Forward span from start → end. If end < start, the area
      // wraps S/F — split the test into [start, total) ∪ [0, end].
      if (L.startArc <= L.endArc) {
        if (a >= L.startArc && a <= L.endArc) areaText = L.text;
      } else {
        if (a >= L.startArc || a <= L.endArc) areaText = L.text;
      }
    }
  }
  return { corner: bestCornerText, area: areaText };
}

// AABB containment with a margin — used for ribbon early-out.
function bboxNearPoint(bb, p, margin) {
  if (!bb) return true;
  return p.x >= bb.minX - margin && p.x <= bb.maxX + margin
      && p.y >= bb.minY - margin && p.y <= bb.maxY + margin;
}

// Wall collision: walk wall segments in the 3×3 cell neighborhood,
// dedupe by wall (each wall produces at most one push from its single
// nearest segment), and apply the push for any wall whose nearest
// segment is closer than thickness/2 + body radius. No bounce — wall
// hits scrub velocity along the wall (~5% per contact frame).
//
// When no walls are authored, falls back to the legacy concentric ring
// wall so containment still works on tracks built without walls.
export function applyWallCollision(car, track, geometry) {
  if (!geometry.walls.length) {
    return applyLegacyRingWallCollision(car, track, geometry);
  }
  const idx = geometry.spatialIndex;
  if (!idx) return applyWallCollisionNoIndex(car, track, geometry);

  const W = geometry.walls.length;
  ensureWallScratch(W);
  for (let i = 0; i < W; i++) _wallScratchDist[i] = Infinity;

  const cellKeyFn = idx.key;
  const gx = Math.floor(car.pos.x / idx.cellSize);
  const gy = Math.floor(car.pos.y / idx.cellSize);
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const arr = idx.segCells.get(cellKeyFn(gx + dx, gy + dy));
      if (!arr) continue;
      for (let i = 0; i < arr.length; i++) {
        const e = arr[i];
        if (e.kind !== 'wall') continue;
        const wall = geometry.walls[e.ownerIdx];
        const dense = wall.dense;
        const a = dense[e.segIdx];
        const b = dense[(e.segIdx + 1) % dense.length];
        closestPointOnSegmentInto(car.pos, a, b, _segScratch);
        if (_segScratch.dist < _wallScratchDist[e.ownerIdx]) {
          _wallScratchDist[e.ownerIdx] = _segScratch.dist;
          _wallScratchPx[e.ownerIdx]   = _segScratch.point.x;
          _wallScratchPy[e.ownerIdx]   = _segScratch.point.y;
        }
      }
    }
  }

  let collidedAny = false;
  for (let wi = 0; wi < W; wi++) {
    const dist = _wallScratchDist[wi];
    if (dist === Infinity) continue;
    const wall = geometry.walls[wi];
    const collisionDist = wall.thickness / 2 + CAR_BODY_RADIUS;
    if (dist >= collisionDist) continue;

    // Push direction: from nearest wall point outward to the car. Works
    // whether the car is inside a closed loop (outer barrier) or
    // outside one (inner barrier of a roval).
    const dxv = car.pos.x - _wallScratchPx[wi];
    const dyv = car.pos.y - _wallScratchPy[wi];
    const len = Math.hypot(dxv, dyv) || 1e-6;
    const nx = dxv / len, ny = dyv / len;

    const overlap = collisionDist - dist;
    car.pos.x += nx * overlap;
    car.pos.y += ny * overlap;

    // vDotN < 0 means the car is moving toward the wall. Kill that
    // component, scrub the tangential, damp spin. Damage is taken
    // from the impulse magnitude (mass × |vDotN|) — the wall is
    // immovable, so the full Δv on the contact normal converts to
    // damage. condition.js gates micro-contacts so a wall slide at
    // 0.5 m/s doesn't accumulate noise.
    const vDotN = car.vel.x * nx + car.vel.y * ny;
    if (vDotN < 0) {
      const impulse = car.spec.mass * (-vDotN);
      applyImpactDamage(car, impulse);
      car.vel.x -= vDotN * nx;
      car.vel.y -= vDotN * ny;
      car.vel.x *= 0.95;
      car.vel.y *= 0.95;
      car.angVel *= 0.92;
    }
    collidedAny = true;
  }
  return collidedAny;
}

// Legacy concentric-ring wall — used only when no authored walls
// exist. Same physics: push back to wall edge, kill outward velocity.
function applyLegacyRingWallCollision(car, track, geometry) {
  const surfaces = track.surfaces || [];
  const last = surfaces[surfaces.length - 1];
  if (!last) return false;
  const wallEdge = last.edgeDist;
  const np = nearestPointOnDense(car.pos, geometry.centerline, true);
  if (!np || np.dist <= wallEdge) return false;

  const dx = car.pos.x - np.point.x;
  const dy = car.pos.y - np.point.y;
  const len = Math.hypot(dx, dy) || 1e-6;
  const nx = dx / len, ny = dy / len;

  const overlap = np.dist - wallEdge;
  car.pos.x -= nx * overlap;
  car.pos.y -= ny * overlap;

  const vDotN = car.vel.x * nx + car.vel.y * ny;
  if (vDotN > 0) {
    const impulse = car.spec.mass * vDotN;
    applyImpactDamage(car, impulse);
    car.vel.x -= vDotN * nx;
    car.vel.y -= vDotN * ny;
    car.vel.x *= 0.95;
    car.vel.y *= 0.95;
    car.angVel *= 0.92;
  }
  return true;
}

// ---------- fallback paths (no spatial index) ----------
//
// Kept as a defensive code path for any future caller that builds
// geometry without going through buildDensifiedTrack (e.g., a unit
// test or a hand-rolled fixture). Production tracks always have an
// index.

function surfaceAtNoIndex(pos, track, geometry) {
  const patches = geometry.patches;
  const cl = nearestPointOnDense(pos, geometry.centerline, true);
  if (cl) {
    const halfArr = geometry.centerlineEdges?.halfWidths;
    const localHalf = halfArr?.[cl.segmentIdx] ?? (track.trackline?.width ?? 16) / 2;
    if (cl.dist <= localHalf) return 'tarmac';
  }
  for (let i = patches.length - 1; i >= 0; i--) {
    if (patchLayer(patches[i]) !== 'above') continue;
    if (patchHits(patches[i], pos)) return validSurface(patches[i].surface);
  }
  if (geometry.pitLane) {
    const np = nearestPointOnDense(pos, geometry.pitLane.dense, false);
    if (np) {
      const localHalf = geometry.pitLane.halfWidths?.[np.segmentIdx] ?? geometry.pitLane.halfWidth;
      if (np.dist <= localHalf) return 'tarmac';
    }
  }
  for (const ribbon of geometry.ribbons) {
    const np = nearestPointOnDense(pos, ribbon.dense, false);
    if (np && np.dist <= ribbon.halfWidth) return 'tarmac';
  }
  for (let i = patches.length - 1; i >= 0; i--) {
    if (patchLayer(patches[i]) !== 'merge') continue;
    if (patchHits(patches[i], pos)) return validSurface(patches[i].surface);
  }
  for (let i = patches.length - 1; i >= 0; i--) {
    if (patchLayer(patches[i]) !== 'below') continue;
    if (patchHits(patches[i], pos)) return validSurface(patches[i].surface);
  }
  return validSurface(track.defaultSurface || 'grass');
}

function applyWallCollisionNoIndex(car, track, geometry) {
  let collidedAny = false;
  for (const wall of geometry.walls) {
    const collisionDist = wall.thickness / 2 + CAR_BODY_RADIUS;
    const np = nearestPointOnDense(car.pos, wall.dense, wall.closed);
    if (!np || np.dist >= collisionDist) continue;
    const dx = car.pos.x - np.point.x;
    const dy = car.pos.y - np.point.y;
    const len = Math.hypot(dx, dy) || 1e-6;
    const nx = dx / len, ny = dy / len;
    const overlap = collisionDist - np.dist;
    car.pos.x += nx * overlap;
    car.pos.y += ny * overlap;
    const vDotN = car.vel.x * nx + car.vel.y * ny;
    if (vDotN < 0) {
      car.vel.x -= vDotN * nx;
      car.vel.y -= vDotN * ny;
      car.vel.x *= 0.95;
      car.vel.y *= 0.95;
      car.angVel *= 0.92;
    }
    collidedAny = true;
  }
  return collidedAny;
}
